import { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaArrowRight } from 'react-icons/fa';

const MatchActivity = ({ initialContent, onContentChange }) => {
  const [content, setContent] = useState({
    wordPairs: [
      { 
        id: 1, 
        keyWord: '', 
        valueWord: ''
      }
    ]
  });

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addWordPair = () => {
    const newId = Math.max(...content.wordPairs.map(p => p.id), 0) + 1;
    const newWordPairs = [...content.wordPairs, { 
      id: newId, 
      keyWord: '', 
      valueWord: ''
    }];
    setContent({ ...content, wordPairs: newWordPairs });
  };

  const removeWordPair = (index) => {
    if (content.wordPairs.length > 1) {
      const newWordPairs = content.wordPairs.filter((_, i) => i !== index);
      setContent({ ...content, wordPairs: newWordPairs });
    }
  };

  const updateWordPair = (index, field, value) => {
    const newWordPairs = [...content.wordPairs];
    newWordPairs[index] = { ...newWordPairs[index], [field]: value };
    setContent({ ...content, wordPairs: newWordPairs });
  };

  const validate = () => {
    const errors = [];
    
    if (!content.wordPairs || content.wordPairs.length === 0) {
      errors.push('At least one word pair is required');
    }

    content.wordPairs?.forEach((pair, index) => {
      if (!pair.keyWord?.trim()) {
        errors.push(`Word pair ${index + 1}: Key word is required`);
      }
      if (!pair.valueWord?.trim()) {
        errors.push(`Word pair ${index + 1}: Value word is required`);
      }
    });

    // Check for duplicate key words
    const keyWords = content.wordPairs?.map(pair => pair.keyWord?.trim().toLowerCase()).filter(Boolean);
    const duplicateKeys = keyWords?.filter((word, index) => keyWords.indexOf(word) !== index);
    if (duplicateKeys?.length > 0) {
      errors.push('Duplicate key words found. Each key word must be unique.');
    }

    // Check for duplicate value words
    const valueWords = content.wordPairs?.map(pair => pair.valueWord?.trim().toLowerCase()).filter(Boolean);
    const duplicateValues = valueWords?.filter((word, index) => valueWords.indexOf(word) !== index);
    if (duplicateValues?.length > 0) {
      errors.push('Duplicate value words found. Each value word must be unique.');
    }

    return errors;
  };

  return (
    <div className="space-y-6">
      {/* Word Pairs Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              🔗 <span className="ml-2">Match Word Pairs</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">{content.wordPairs.length} pair{content.wordPairs.length !== 1 ? 's' : ''} configured</p>
          </div>
          <button
            type="button"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
            onClick={addWordPair}
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add Word Pair
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {content.wordPairs.map((pair, index) => (
            <div key={pair.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4">
                {/* Index */}
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                
                {/* Key Word Input */}
                <div className="flex-1">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Key word (left column) *"
                    value={pair.keyWord || ''}
                    onChange={(e) => updateWordPair(index, 'keyWord', e.target.value)}
                  />
                </div>
                
                {/* Arrow Icon */}
                <div className="flex-shrink-0 text-gray-400">
                  <FaArrowRight className="w-4 h-4" />
                </div>
                
                {/* Value Word Input */}
                <div className="flex-1">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                    placeholder="Value word (right column) *"
                    value={pair.valueWord || ''}
                    onChange={(e) => updateWordPair(index, 'valueWord', e.target.value)}
                  />
                </div>
                
                {/* Remove Button */}
                {content.wordPairs.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeWordPair(index)}
                    title="Remove Word Pair"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      
      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

// Export validation function
export const validateMatchActivity = (content) => {
  const errors = [];
  
  if (!content.wordPairs || content.wordPairs.length === 0) {
    errors.push('At least one word pair is required');
  }

  content.wordPairs?.forEach((pair, index) => {
    if (!pair.keyWord?.trim()) {
      errors.push(`Word pair ${index + 1}: Key word is required`);
    }
    if (!pair.valueWord?.trim()) {
      errors.push(`Word pair ${index + 1}: Value word is required`);
    }
  });

  // Check for duplicate key words
  const keyWords = content.wordPairs?.map(pair => pair.keyWord?.trim().toLowerCase()).filter(Boolean);
  const duplicateKeys = keyWords?.filter((word, index) => keyWords.indexOf(word) !== index);
  if (duplicateKeys?.length > 0) {
    errors.push('Duplicate key words found. Each key word must be unique.');
  }

  // Check for duplicate value words
  const valueWords = content.wordPairs?.map(pair => pair.valueWord?.trim().toLowerCase()).filter(Boolean);
  const duplicateValues = valueWords?.filter((word, index) => valueWords.indexOf(word) !== index);
  if (duplicateValues?.length > 0) {
    errors.push('Duplicate value words found. Each value word must be unique.');
  }

  return errors;
};

export default MatchActivity;
